// constants
import { ENTITIES_STATUSES } from 'constants/index'
import { EACH_PERIOD_ENDS_TYPE_VALUES, REPEAT_VALUES } from 'constants/kpi'
// utils
import first from 'lodash/fp/first'
import getOr from 'lodash/fp/getOr'
import isEmpty from 'lodash/fp/isEmpty'
import last from 'lodash/fp/last'
import map from 'lodash/fp/map'
import minBy from 'lodash/fp/minBy'
import moment from 'moment'
import pipe from 'lodash/fp/pipe'
import { atLeast, generateRanges, getValueFromOption, safeGetOr } from 'utils/index'

const getItselfOrValue = optionOrValue => getOr(optionOrValue, 'value', optionOrValue)

const generateWeeklyKPIRecurrenceRanges = (data, startPoint) => {
  const { days, captureDuration, captureType, repeatEveryValue } = data

  const repeatEvery = parseInt(repeatEveryValue)

  const closestDay = pipe(
    map(dayName => startPoint.clone().day(dayName)),
    map(momentInstance => momentInstance.startOf('day')),
    map(momentInstance => {
      const shouldGoToNextWeek = momentInstance.isSameOrBefore(startPoint, 'day')

      // or jump to next week
      return shouldGoToNextWeek ? momentInstance.clone().add(7, 'day') : momentInstance
    }),
    minBy(momentInstance => momentInstance.valueOf())
  )(days)

  let lastDayOfThePeriod = closestDay.clone().add(repeatEvery - 1, 'week')
  let nextStartPoint = closestDay.clone().add(repeatEvery - 1, 'week')

  const isStartPointEqualToLastDay = startPoint.isSame(lastDayOfThePeriod, 'day')
  const isStartPointEqualToNextStartPoint = startPoint.isSame(nextStartPoint, 'day')

  if (!isStartPointEqualToLastDay) {
    lastDayOfThePeriod = lastDayOfThePeriod.subtract(1, 'day')
  }

  if (isStartPointEqualToNextStartPoint) {
    nextStartPoint = nextStartPoint.add(1, 'day')
  }

  let dueDate

  if (captureDuration && captureType) {
    dueDate = lastDayOfThePeriod.clone().add(captureDuration, captureType)
  }

  return {
    startPoint,
    lastDayOfThePeriod,
    nextStartPoint,
    dueDate
  }
}

const generateMonthlyKPIRecurrenceRanges = (data, startPoint) => {
  const { repeatEveryValue, eachPeriodEndsType, captureDuration, captureType } = data

  const result = {
    startPoint,
    lastDayOfThePeriod: null
  }

  const repeatEvery = parseInt(repeatEveryValue)

  if (getItselfOrValue(eachPeriodEndsType) === EACH_PERIOD_ENDS_TYPE_VALUES.THE_SAME_DAY) {
    const nextStartPoint = startPoint
      .clone()
      .add(repeatEvery, 'month')
      .startOf('day')

    result.nextStartPoint = nextStartPoint
    result.lastDayOfThePeriod = nextStartPoint.clone().subtract(1, 'day')
  }

  if (getItselfOrValue(eachPeriodEndsType) === EACH_PERIOD_ENDS_TYPE_VALUES.LAST_DAY) {
    result.nextStartPoint = startPoint
      .clone()
      .add(repeatEvery, 'month')
      .startOf('month')

    result.lastDayOfThePeriod = startPoint
      .clone()
      .add(atLeast(0)(repeatEvery - 1), 'month')
      .endOf('month')
  }

  if (captureDuration && captureType) {
    result.dueDate = result.lastDayOfThePeriod.clone().add(captureDuration, captureType)
  }

  return result
}

export const generateKPIRecurrenceRanges = (data, startFrom) => {
  if (isEmpty(data)) {
    return {}
  }

  const { repeatEveryType } = data

  const startPoint = moment(startFrom).startOf('day')

  if (getItselfOrValue(repeatEveryType) === REPEAT_VALUES.WEEKLY) {
    return {
      ...generateWeeklyKPIRecurrenceRanges(data, startPoint),
      id: startPoint.valueOf(),
      type: REPEAT_VALUES.WEEKLY
    }
  }

  if (getItselfOrValue(repeatEveryType) === REPEAT_VALUES.MONTHLY) {
    return {
      ...generateMonthlyKPIRecurrenceRanges(data, startPoint),
      id: startPoint.valueOf(),
      type: REPEAT_VALUES.MONTHLY
    }
  }
}

export const normalizeRecurrenceData = data => ({
  ...data,
  repeatEveryType: getValueFromOption(data.repeatEveryType),
  eachPeriodEndsType: getValueFromOption(data.eachPeriodEndsType),
  captureType: getValueFromOption(data.captureType)
})

export const getClosestRange = (recurrence, resultTarget) => {
  const { ranges } = generateRanges({ recurrence })

  const capturedResults = safeGetOr([], 'capturedResults')(resultTarget)

  const filteredRanges = ranges.map(range => {
    const capturedResult = !!capturedResults.find(({ id }) => id === range.id)

    return { ...range, status: capturedResult ? ENTITIES_STATUSES.COMPLETE : ENTITIES_STATUSES.IN_PROGRESS }
  })

  let closestRange

  const overdueRanges = filteredRanges.filter(range => range.status === ENTITIES_STATUSES.IN_PROGRESS)
  const completedRanges = filteredRanges.filter(range => range.status === ENTITIES_STATUSES.COMPLETE)

  if (!isEmpty(overdueRanges)) {
    closestRange = first(overdueRanges)
  }
  if (isEmpty(overdueRanges) && !isEmpty(completedRanges)) {
    closestRange = last(completedRanges)
  }

  if (!closestRange) {
    return null
  }

  return closestRange
}
